import React, { useState, useEffect } from 'react';
import { FaKey, FaList, FaLock } from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenFields } from '../hooks/useHiddenFields';
import { getToken } from '../utils/authStorage';
import '../css/standard-components.css';

interface Permission {
  id: number;
  name: string;
  description: string;
  module: string;
  action: string;
  createdAt: string;
  updatedAt: string;
}

const Permissions: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenFields();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('');

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const response = await fetch(`${API_BASE_URL}/permissions`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPermissions(Array.isArray(data) ? data : data.permissions || []);
      } else {
        console.error('Failed to load permissions:', response.statusText);
        // Fall back to mock data if API fails
        setPermissions(getMockPermissions());
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Use mock data when API is not available
      setPermissions(getMockPermissions());
    } finally {
      setLoading(false);
    }
  };

  const getMockPermissions = (): Permission[] => {
    return [
        {
          id: 1,
          name: 'view_projects',
          description: 'View all projects',
          module: 'projects',
          action: 'view',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 2,
          name: 'create_projects',
          description: 'Create new projects',
          module: 'projects',
          action: 'create',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 3,
          name: 'edit_projects',
          description: 'Edit existing projects',
          module: 'projects',
          action: 'edit',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 4,
          name: 'delete_projects',
          description: 'Delete projects',
          module: 'projects',
          action: 'delete',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 5,
          name: 'view_bookings',
          description: 'View all bookings',
          module: 'bookings',
          action: 'view',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 6,
          name: 'create_bookings',
          description: 'Create new bookings',
          module: 'bookings',
          action: 'create',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 7,
          name: 'edit_bookings',
          description: 'Edit existing bookings',
          module: 'bookings',
          action: 'edit',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 8,
          name: 'delete_bookings',
          description: 'Delete bookings',
          module: 'bookings',
          action: 'delete',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 9,
          name: 'view_ledger',
          description: 'View ledger information',
          module: 'ledger',
          action: 'view',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 10,
          name: 'manage_finances',
          description: 'Manage financial records',
          module: 'ledger',
          action: 'manage',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 11,
          name: 'approve_transactions',
          description: 'Approve financial transactions',
          module: 'approval',
          action: 'approve',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 12,
          name: 'manage_users',
          description: 'Manage user accounts',
          module: 'auth',
          action: 'manage',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 13,
          name: 'manage_roles',
          description: 'Manage user roles',
          module: 'auth',
          action: 'manage',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        },
        {
          id: 14,
          name: 'view_reports',
          description: 'View system reports',
          module: 'reports',
          action: 'view',
          createdAt: '2025-08-31T00:00:00Z',
          updatedAt: '2025-08-31T00:00:00Z'
        }
      ];
  };

  const getUniqueModules = () => {
    const moduleSet = new Set(permissions?.map(p => p.module) || []);
    return Array.from(moduleSet);
  };

  const filteredPermissions = (permissions || []).filter(permission => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = !selectedModule || permission.module === selectedModule;
    
    // Hide specific permissions when hidden mode is not active
    if (!isHiddenModeActive) {
      const hiddenPermissions = ['broker.', 'expense.', 'partner.', 'income.'];
      const isHiddenPermission = hiddenPermissions.some(prefix => permission.name.startsWith(prefix));
      if (isHiddenPermission) {
        return false;
      }
    }
    
    return matchesSearch && matchesModule;
  });

  const groupedPermissions = filteredPermissions.reduce((groups, permission) => {
    const module = permission.module;
    if (!groups[module]) {
      groups[module] = [];
    }
    groups[module].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{textAlign: 'center', padding: '60px', color: '#6b7280'}}>
          Loading permissions...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <input
            type="text"
            placeholder="Search permissions by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "6px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "13px",
              minWidth: "400px",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Module:</span>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Modules</option>
              {getUniqueModules().map(module => (
                <option key={module} value={module}>
                  {module.charAt(0).toUpperCase() + module.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CompactActionBar>

      {Object.keys(groupedPermissions).length === 0 ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <FaLock style={{fontSize: '48px', marginBottom: '16px', opacity: '0.5', color: '#6b7280'}} />
          <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>No permissions found</h3>
          <p style={{margin: 0, color: '#6b7280'}}>No permissions match your current search criteria.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: '24px' }}>
        {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
          <div key={module} style={{ marginBottom: '32px' }}>
            <div className="flex items-center gap-3 mb-6">
              <FaList style={{color: '#4f46e5', fontSize: '20px'}} />
              <h2 style={{margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827'}}>
                {module.charAt(0).toUpperCase() + module.slice(1)} Module
              </h2>
              <span style={{
                background: '#f3f4f6',
                color: '#374151',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {modulePermissions.length} permissions
              </span>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px'}}>
              {modulePermissions.map(permission => (
                <div key={permission.id} style={{
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}>
                  <div className="flex justify-between items-start mb-3">
                    <h3 style={{
                      margin: 0,
                      fontWeight: '600',
                      color: '#111827',
                      fontFamily: 'monospace',
                      background: '#e5e7eb',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      {permission.name}
                    </h3>
                    <span className={`status-badge status-${permission.action === 'view' ? 'ready' : permission.action === 'create' ? 'active' : permission.action === 'edit' ? 'pending' : 'expired'}`}>
                      {permission.action}
                    </span>
                  </div>
                  <p style={{
                    margin: '0 0 16px 0',
                    color: '#6b7280',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {permission.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#9ca3af'
                  }}>
                    <span>ID: {permission.id}</span>
                    <span>Module: {permission.module}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
};

export default Permissions;
